import React from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import { ArrowMove20Regular } from '@fluentui/react-icons';

// Per-line "Move to phase" affordance shown on the phase cards' rich line rows.
// Lists every phase; the line's current home is shown checked + disabled. There
// is no "unschedule" target — every line always lives in a phase. Selecting one
// calls onMove with the section id — the caller persists via assignLineSection.
// Hidden entirely on a billed phase's lines (the backend rejects the move
// anyway); the caller controls `disabled`.

export interface MovePhaseOption {
  id: string;
  label: string;
  /** Billed phases are frozen — can't move a line into them. */
  disabled?: boolean;
}

interface MovePhaseMenuProps {
  phases: MovePhaseOption[];
  /** The line's current section id, or null when unscheduled. */
  currentSectionId: string | null;
  onMove: (sectionId: string | null) => void;
  /** Disable the whole control (e.g. the line sits in a billed phase). */
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const MovePhaseMenu: React.FC<MovePhaseMenuProps> = ({
  phases,
  currentSectionId,
  onMove,
  disabled,
  size = 'small',
}) => (
  <Menu positioning="below-end">
    <MenuTrigger disableButtonEnhancement>
      <Tooltip content="Move to phase" relationship="label">
        <Button
          appearance="subtle"
          size={size}
          icon={<ArrowMove20Regular />}
          aria-label="Move to phase"
          disabled={disabled}
        />
      </Tooltip>
    </MenuTrigger>
    <MenuPopover>
      <MenuList>
        {phases.map((p) => (
          <MenuItem
            key={p.id}
            disabled={p.disabled || p.id === currentSectionId}
            onClick={() => onMove(p.id)}
          >
            {p.id === currentSectionId ? `${p.label} (current)` : p.label}
          </MenuItem>
        ))}
      </MenuList>
    </MenuPopover>
  </Menu>
);

export default MovePhaseMenu;
